import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Button,
  Input,
  Textarea,
  Select,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Switch,
  Divider,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiSlash,
  FiActivity,
  FiMap,
  FiAlertTriangle,
  FiCalendar,
  FiArrowRight,
} from 'react-icons/fi';
import {
  listStudentStatuses,
  upsertStudentStatus,
  deleteStudentStatus,
  listPedagogicalExits,
} from 'services/qrService';
import { normalizeCourseName } from 'utils/courseUtils';

const TYPE_CONFIG = {
  suspended:        { label: 'Suspendido',        pastel: 'rose',   ink: '#DC2626', icon: FiSlash },
  medical_leave:    { label: 'Licencia Médica',   pastel: 'butter', ink: '#D97706', icon: FiActivity },
  pedagogical_exit: { label: 'Salida Pedagógica', pastel: 'lav',    ink: '#6D28D9', icon: FiMap },
  sick_exit:        { label: 'Salida por Enfermedad', pastel: 'mint', ink: '#0F766E', icon: FiActivity },
};

const StatusChip = ({ cfg, fontSize = '12px' }) => (
  <HStack
    spacing={1.5} px="10px" py="4px" borderRadius="999px"
    bg={`${cfg.pastel}.bg`} color={`${cfg.pastel}.ink`} display="inline-flex" w="fit-content"
  >
    <Icon as={cfg.icon} boxSize={3} />
    <Text fontSize={fontSize} fontWeight="700">{cfg.label}</Text>
  </HStack>
);

const EMPTY_FORM = {
  id: null,
  type: 'suspended',
  reason: '',
  notes: '',
  starts_at: '',
  ends_at: '',
  authorized_by: '',
  active: true,
};

const toDatetimeLocal = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// Un estado es "vigente" si está activo y la fecha actual cae dentro de su rango (los extremos son opcionales)
const isCurrent = (status) => {
  if (!status.active) return false;
  const now = new Date();
  if (status.starts_at && new Date(status.starts_at) > now) return false;
  if (status.ends_at && new Date(status.ends_at) < now) return false;
  return true;
};

const StudentStatusCard = ({ customer }) => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [groupExit, setGroupExit] = useState(null);
  const [groupExitLoading, setGroupExitLoading] = useState(true);

  const { isOpen: isFormOpen, onOpen: onOpenForm, onClose: onCloseForm } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();

  const toast = useToast();

  const wpUserId = customer?.id;
  const rut = customer?.username;
  const groupName = customer?.meta_data?.find(m => m.key === 'group_name')?.value
    ?? customer?.meta_data?.find(m => m.key === 'grupo')?.value
    ?? null;

  const fetchStatuses = useCallback(async () => {
    if (!wpUserId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listStudentStatuses({ wp_user_id: wpUserId });
      setStatuses(res.data ?? []);
    } catch {
      setError('No se pudo cargar el estado del alumno.');
    } finally {
      setLoading(false);
    }
  }, [wpUserId]);

  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);

  // Salida pedagógica grupal vigente para el curso del alumno (solo lectura)
  useEffect(() => {
    if (!groupName) { setGroupExitLoading(false); return; }
    (async () => {
      setGroupExitLoading(true);
      try {
        const res = await listPedagogicalExits({ active: true });
        const now = new Date();
        const normalizedGroup = normalizeCourseName(groupName);
        const exit = (res.data ?? []).find(e =>
          (e.group_names ?? []).some(g => g === groupName || normalizeCourseName(g) === normalizedGroup) &&
          new Date(e.starts_at) <= now && new Date(e.ends_at) >= now
        );
        setGroupExit(exit ?? null);
      } catch {
        setGroupExit(null);
      } finally {
        setGroupExitLoading(false);
      }
    })();
  }, [groupName]);

  // Estado individual vigente (mayor prioridad si hay varios)
  const current = useMemo(() => {
    return statuses
      .filter(isCurrent)
      .sort((a, b) => new Date(b.starts_at ?? 0) - new Date(a.starts_at ?? 0))[0] ?? null;
  }, [statuses]);

  const history = useMemo(
    () => statuses.filter(s => s.id !== current?.id),
    [statuses, current]
  );

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    onOpenForm();
  };

  const openEdit = (status) => {
    setForm({
      id: status.id,
      type: status.type,
      reason: status.reason ?? '',
      notes: status.notes ?? '',
      starts_at: toDatetimeLocal(status.starts_at),
      ends_at: toDatetimeLocal(status.ends_at),
      authorized_by: status.authorized_by ?? '',
      active: status.active,
    });
    setEditing(status.id);
    onOpenForm();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await upsertStudentStatus({
        id: form.id,
        wp_user_id: wpUserId,
        rut,
        type: form.type,
        reason: form.reason.trim() || null,
        notes: form.notes.trim() || null,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        authorized_by: form.authorized_by.trim() || null,
        active: form.active,
      });
      toast({ title: editing ? 'Estado actualizado' : 'Estado creado', status: 'success', duration: 2500 });
      const deactivated = res?.data?.deactivated ?? [];
      if (deactivated.length > 0) {
        const labels = deactivated.map(s => TYPE_CONFIG[s.type]?.label ?? s.type).join(', ');
        toast({
          title: 'Estado anterior desactivado',
          description: `Se desactivó automáticamente: ${labels} (un alumno solo puede tener un estado vigente a la vez).`,
          status: 'info',
          duration: 5000,
        });
      }
      onCloseForm();
      fetchStatuses();
    } catch (e) {
      toast({
        title: 'Error al guardar',
        description: e?.response?.data?.errors ? JSON.stringify(e.response.data.errors) : e.message,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (status) => {
    setSaving(true);
    try {
      await upsertStudentStatus({
        id: status.id,
        wp_user_id: status.wp_user_id,
        rut: status.rut,
        type: status.type,
        reason: status.reason,
        notes: status.notes,
        starts_at: status.starts_at,
        ends_at: status.ends_at,
        authorized_by: status.authorized_by,
        active: false,
      });
      toast({ title: 'Estado desactivado', status: 'success', duration: 2500 });
      fetchStatuses();
    } catch {
      toast({ title: 'Error al desactivar', status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (status) => {
    setDeleteTarget(status);
    onOpenDelete();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(deleteTarget.id);
    try {
      await deleteStudentStatus(deleteTarget.id);
      toast({ title: 'Estado eliminado', status: 'success', duration: 2500 });
      onCloseDelete();
      fetchStatuses();
    } catch {
      toast({ title: 'Error al eliminar', status: 'error', duration: 3000 });
    } finally {
      setDeleting(null);
    }
  };

  const setF = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const currentCfg = current ? TYPE_CONFIG[current.type] : null;

  return (
    <Box
      fontFamily="jakarta"
      bg="white" border="1px solid" borderColor="cream.line" borderRadius="20px"
      p={{ base: 4, md: 6 }} boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
    >
      {/* ── Estado individual del alumno ── */}
      <HStack justify="space-between" align="center" mb={4}>
        <Text fontWeight="800" fontSize="15px" color="ink.900">
          Estado de acceso
        </Text>
        <Button
          h="32px" px={3} borderRadius="9px" fontSize="12.5px" fontWeight="700"
          border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
          _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
          leftIcon={<FiPlus />} onClick={openCreate}
        >
          {current ? 'Nuevo estado' : 'Agregar estado'}
        </Button>
      </HStack>

      {error && (
        <Box bg="rose.bg" borderRadius="12px" p={3} mb={3}>
          <Text fontSize="13px" color="rose.ink" fontWeight="600">{error}</Text>
        </Box>
      )}

      {loading ? (
        <HStack py={4} spacing={3}>
          <Spinner size="sm" color="coral.500" />
          <Text fontSize="13.5px" color="ink.400">Cargando estado…</Text>
        </HStack>
      ) : current ? (
        <Box bg={`${currentCfg.pastel}.bg`} p={4} borderRadius="14px" mb={3}>
          <VStack align="flex-start" spacing={2}>
            <HStack justify="space-between" w="100%">
              <StatusChip cfg={currentCfg} />
              <HStack spacing={1}>
                <Tooltip label="Editar">
                  <IconButton icon={<FiEdit3 />} size="xs" variant="ghost" color="ink.700" onClick={() => openEdit(current)} aria-label="Editar" />
                </Tooltip>
                <Tooltip label="Quitar estado (desactivar)">
                  <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" color="#DC2626"
                    onClick={() => handleDeactivate(current)} isLoading={saving} aria-label="Quitar estado" />
                </Tooltip>
              </HStack>
            </HStack>
            {current.reason && (
              <Text fontSize="13.5px" color="ink.900"><b>Motivo:</b> {current.reason}</Text>
            )}
            {current.notes && (
              <Text fontSize="12px" color="ink.400">{current.notes}</Text>
            )}
            <HStack spacing={4} flexWrap="wrap">
              {(current.starts_at || current.ends_at) && (
                <HStack spacing={1}>
                  <Icon as={FiCalendar} boxSize={3} color="ink.400" />
                  <Text fontSize="12px" color="ink.400">
                    {formatDate(current.starts_at)} → {current.ends_at ? formatDate(current.ends_at) : 'Indefinido'}
                  </Text>
                </HStack>
              )}
              {current.authorized_by && (
                <Text fontSize="12px" color="ink.400"><b>Autorizado por:</b> {current.authorized_by}</Text>
              )}
            </HStack>
          </VStack>
        </Box>
      ) : (
        <Text fontSize="13.5px" color="ink.400" mb={3}>Sin estados especiales activos.</Text>
      )}

      {/* ── Salida pedagógica grupal del curso (solo lectura) ── */}
      {!groupExitLoading && groupExit && (
        <Box bg="sky.bg" p={4} borderRadius="14px" mb={3}>
          <VStack align="flex-start" spacing={2}>
            <HStack>
              <Icon as={FiMap} color="sky.ink" boxSize={4} />
              <Text fontSize="13.5px" fontWeight="700" color="sky.ink">
                Salida pedagógica del curso{groupExit.title ? `: ${groupExit.title}` : ''}
              </Text>
            </HStack>
            <HStack spacing={1}>
              <Icon as={FiCalendar} boxSize={3} color="sky.ink" />
              <Text fontSize="12px" color="sky.ink">
                {formatDate(groupExit.starts_at)} → {formatDate(groupExit.ends_at)}
              </Text>
            </HStack>
            {groupExit.authorized_by && (
              <Text fontSize="12px" color="sky.ink"><b>Autorizado por:</b> {groupExit.authorized_by}</Text>
            )}
            <Button as={RouterLink} to="/accesos-qr" size="xs" variant="link" color="sky.ink" fontWeight="700" rightIcon={<FiArrowRight />}>
              Gestionar salidas pedagógicas
            </Button>
          </VStack>
        </Box>
      )}

      {/* ── Historial de estados ── */}
      {history.length > 0 && (
        <Box mt={4}>
          <Text fontSize="13.5px" fontWeight="700" color="ink.900" mb={2}>
            Historial de estados
          </Text>
          <TableContainer border="1px solid" borderColor="cream.line" borderRadius="14px">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr bg="cream.bg2">
                  <Th color="ink.400" fontSize="11px">Tipo</Th>
                  <Th color="ink.400" fontSize="11px" display={{ base: 'none', md: 'table-cell' }}>Motivo</Th>
                  <Th color="ink.400" fontSize="11px" display={{ base: 'none', lg: 'table-cell' }}>Vigencia</Th>
                  <Th color="ink.400" fontSize="11px">Estado</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {history.map(status => {
                  const cfg = TYPE_CONFIG[status.type] ?? TYPE_CONFIG.suspended;
                  return (
                    <Tr key={status.id} _hover={{ bg: 'coral.tint' }} opacity={status.active ? 1 : 0.6}>
                      <Td>
                        <StatusChip cfg={cfg} fontSize="11px" />
                      </Td>
                      <Td fontSize="12px" color="ink.400" display={{ base: 'none', md: 'table-cell' }} maxW="200px">
                        <Text noOfLines={1}>{status.reason || '—'}</Text>
                      </Td>
                      <Td fontSize="12px" color="ink.400" display={{ base: 'none', lg: 'table-cell' }}>
                        {formatDate(status.starts_at)} → {formatDate(status.ends_at)}
                      </Td>
                      <Td>
                        <Box as="span" px="8px" py="3px" borderRadius="999px" fontSize="11px" fontWeight="700"
                          bg={status.active ? 'mint.bg' : 'cream.bg2'} color={status.active ? 'mint.ink' : 'ink.400'}>
                          {status.active ? 'Activo' : 'Inactivo'}
                        </Box>
                      </Td>
                      <Td isNumeric>
                        <HStack spacing={1} justify="flex-end">
                          <Tooltip label="Editar">
                            <IconButton icon={<FiEdit3 />} size="xs" variant="ghost" color="ink.700" onClick={() => openEdit(status)} aria-label="Editar" />
                          </Tooltip>
                          <Tooltip label="Eliminar">
                            <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" color="#DC2626"
                              onClick={() => confirmDelete(status)} isLoading={deleting === status.id} aria-label="Eliminar" />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── Modal crear / editar ── */}
      <Modal isOpen={isFormOpen} onClose={onCloseForm} isCentered size="md" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent mx={3} fontFamily="jakarta" borderRadius="18px">
          <ModalHeader fontSize="16px" fontWeight="800" color="ink.900">
            {editing ? 'Editar estado' : 'Nuevo estado de alumno'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="12.5px" color="ink.700" fontWeight="600">Tipo de estado</FormLabel>
                <Select size="sm" borderRadius="9px" value={form.type} onChange={setF('type')}>
                  {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                    <option key={key} value={key}>{cfg.label}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="12.5px" color="ink.700" fontWeight="600">Motivo</FormLabel>
                <Input size="sm" borderRadius="9px" placeholder="Ej: Reposo médico"
                  value={form.reason} onChange={setF('reason')} />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="12.5px" color="ink.700" fontWeight="600">Notas internas</FormLabel>
                <Textarea size="sm" borderRadius="9px" rows={2}
                  value={form.notes} onChange={setF('notes')} />
              </FormControl>

              <HStack spacing={3} align="flex-start">
                <FormControl>
                  <FormLabel fontSize="12.5px" color="ink.700" fontWeight="600">Desde</FormLabel>
                  <Input type="datetime-local" size="sm" borderRadius="9px"
                    value={form.starts_at} onChange={setF('starts_at')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="12.5px" color="ink.700" fontWeight="600">Hasta</FormLabel>
                  <Input type="datetime-local" size="sm" borderRadius="9px"
                    value={form.ends_at} onChange={setF('ends_at')} />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="12.5px" color="ink.700" fontWeight="600">Autorizado por</FormLabel>
                <Input size="sm" borderRadius="9px" placeholder="Ej: Dirección Académica"
                  value={form.authorized_by} onChange={setF('authorized_by')} />
              </FormControl>

              <Divider borderColor="cream.line" />

              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel mb={0} fontSize="12.5px" color="ink.700" fontWeight="600">Activo</FormLabel>
                <Switch isChecked={form.active}
                  onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                  colorScheme="orange" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={onCloseForm}>Cancelar</Button>
            <Button
              size="sm" borderRadius="9px" bg="coral.500" color="#fff" fontWeight="700"
              _hover={{ bg: 'coral.600' }}
              onClick={handleSave} isLoading={saving}
            >
              {editing ? 'Actualizar' : 'Crear estado'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Modal eliminar ── */}
      <Modal isOpen={isDeleteOpen} onClose={onCloseDelete} isCentered size="sm">
        <ModalOverlay />
        <ModalContent mx={3} fontFamily="jakarta" borderRadius="18px">
          <ModalHeader fontSize="16px" fontWeight="800" color="ink.900">Eliminar estado</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={3} spacing={3}>
              <Icon as={FiAlertTriangle} color="#DC2626" boxSize={6} />
              <Text fontWeight="700" fontSize="14px" color="ink.900">¿Eliminar este estado?</Text>
            </HStack>
            <Text fontSize="13px" color="ink.400" fontWeight="500">
              Esta acción no se puede deshacer.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={onCloseDelete}>Cancelar</Button>
            <Button
              size="sm" borderRadius="9px" bg="#DC2626" color="#fff" fontWeight="700"
              _hover={{ bg: '#B91C1C' }}
              onClick={handleDelete} isLoading={!!deleting}
            >
              Sí, eliminar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default StudentStatusCard;
